import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { MaskedHeading, Fade, FadeUp, ImageReveal } from "@/components/Reveal";
import { getCase } from "@/lib/api";
import usePageMeta from "@/lib/usePageMeta";

export default function CaseDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);

  usePageMeta({
    title: item ? `Case: ${item.title}` : "Case",
    description: item?.summary || "Udvalgt case fra Hagelund — døre og vinduer, der løfter hjemmet.",
    path: `/cases/${slug}`,
    image: item?.cover_image || undefined,
  });

  useEffect(() => {
    getCase(slug).then(setItem).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-[70svh] flex flex-col items-center justify-center px-6" data-testid="case-not-found">
        <h1 className="font-display text-5xl font-light">Case ikke fundet</h1>
        <Link to="/cases" className="mt-6 text-xs uppercase tracking-label border-b border-foreground/40 pb-1">Tilbage til cases</Link>
      </div>
    );
  }
  if (!item) return <div className="min-h-[70svh]" />;

  return (
    <div data-testid="case-detail-page">
      {/* Full-bleed hero */}
      <section className="relative h-[80svh] min-h-[520px] overflow-hidden">
        <div className="absolute inset-0">
          <img src={item.cover_image} alt={item.title} className="img-cover" />
        </div>
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative h-full mx-auto max-w-[1600px] px-6 md:px-12 flex flex-col justify-end pt-28 pb-16 md:pb-20">
          <Fade delay={0.1}>
            <div className="flex items-center gap-3 text-white/80 text-xs uppercase tracking-label mb-6">
              <span>{item.location}</span><span className="text-accent">&#9670;</span><span>{item.year}</span>
              <span className="text-accent">&#9670;</span><span>{item.category}</span>
            </div>
          </Fade>
          <h1 className="font-display text-white font-light leading-[0.9] text-6xl md:text-9xl tracking-tight">
            <MaskedHeading lines={[item.title]} delay={0.25} />
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-12 py-20 md:py-32">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-7">
            <FadeUp>
              <h2 className="font-display text-3xl md:text-5xl font-light leading-tight text-balance">{item.subtitle}</h2>
              <p className="mt-8 text-muted-foreground font-light text-lg whitespace-pre-line">{item.description}</p>
            </FadeUp>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <FadeUp delay={0.1}>
              <p className="text-xs uppercase tracking-label text-accent mb-6">Løsning</p>
              <ul className="space-y-3">
                {(item.materials || []).map((m) => (
                  <li key={m} className="flex justify-between border-b border-border pb-3 font-light">
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Gallery — editorial alternating rhythm */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-12 pb-24 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-6 auto-rows-auto">
          {(item.images || []).map((img, i) => {
            const pattern = i % 5;
            const span =
              pattern === 0 ? "md:col-span-6 aspect-[16/9]" :
              pattern === 1 ? "md:col-span-3 aspect-[4/5]" :
              pattern === 2 ? "md:col-span-3 aspect-[4/5]" :
              pattern === 3 ? "md:col-span-2 aspect-[3/4]" :
              "md:col-span-4 aspect-[16/10]";
            return <ImageReveal key={img} src={img} alt={`${item.title} ${i + 1}`} className={span} delay={(i % 2) * 0.08} />;
          })}
        </div>
      </section>

      {/* Next / CTA */}
      <section className="bg-foreground text-background py-20 md:py-28">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-8">
          <Link to="/cases" className="inline-flex items-center gap-3 text-xs uppercase tracking-label text-background/70 hover:text-background" data-testid="case-back">
            <ArrowLeft size={16} weight="bold" /> Alle cases
          </Link>
          <h2 className="font-display text-3xl md:text-5xl font-light text-center">Vil du løfte dit hjem?</h2>
          <Link to="/kontakt" data-testid="case-cta" className="group inline-flex items-center gap-3 bg-background text-foreground px-8 py-4 text-xs uppercase tracking-label font-semibold hover:bg-accent hover:text-accent-foreground transition-colors">
            Book rådgivning <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
